/**
 * Form Validation Utilities
 * Provides security validation against code injection, SQL injection, XSS, and script injection
 */

// Common dangerous patterns
const SQL_INJECTION_PATTERNS = [
  /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|TRUNCATE|EXEC|EXECUTE|UNION|JOIN)\b)/gi,
  /(--)|(;)|(\/\*)|(\*\/)/g,
  /(\b(OR|AND)\b\s+\d+\s*=\s*\d+)/gi,
  /(\b(OR|AND)\b\s+['"]?\w+['"]?\s*=\s*['"]?\w+['"]?)/gi,
  /(WAITFOR\s+DELAY)/gi,
  /(BENCHMARK\s*\()/gi,
  /(SLEEP\s*\()/gi,
];

const XSS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /<script[^>]*>/gi,
  /<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi, // onclick, onerror, onload, etc.
  /<iframe[^>]*>/gi,
  /<object[^>]*>/gi,
  /<embed[^>]*>/gi,
  /<link[^>]*>/gi,
  /<style[^>]*>/gi,
  /<img[^>]+onerror/gi,
  /expression\s*\(/gi,
  /url\s*\(\s*['"]?\s*data:/gi, //NOSONAR
];

const CODE_INJECTION_PATTERNS = [
  /eval\s*\(/gi,
  /Function\s*\(/gi,
  /setTimeout\s*\(\s*['"`]/gi,
  /setInterval\s*\(\s*['"`]/gi,
  /new\s+Function\s*\(/gi,
  /document\.write/gi,
  /document\.writeln/gi,
  /innerHTML\s*=/gi,
  /outerHTML\s*=/gi,
  /\.createElement\s*\(/gi,
];

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#96;',
};

export interface ValidationResult {
  isValid: boolean;
  sanitizedValue: string;
  errors: string[];
}

export interface FieldValidationOptions {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  customValidator?: (value: string) => boolean;
  fieldName?: string;
}

/**
 * Escape HTML entities to prevent XSS
 */
export const escapeHtml = (str: string): string => {
  return str.replace(/[&<>"'`/]/g, (char) => HTML_ENTITIES[char] || char); //NOSONAR
};

/**
 * Check for SQL injection patterns
 */
export const containsSqlInjection = (value: string): boolean => {
  return SQL_INJECTION_PATTERNS.some((pattern) => pattern.test(value));
};

/**
 * Check for XSS patterns
 */
export const containsXss = (value: string): boolean => {
  return XSS_PATTERNS.some((pattern) => pattern.test(value));
};

/**
 * Check for code injection patterns
 */
export const containsCodeInjection = (value: string): boolean => {
  return CODE_INJECTION_PATTERNS.some((pattern) => pattern.test(value));
};

/**
 * Sanitize input by removing dangerous characters and patterns
 */
export const sanitizeInput = (value: string): string => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  let sanitized = value;

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, ''); //NOSONAR

  // Remove script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ''); //NOSONAR

  // Remove HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, ''); //NOSONAR

  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, ''); //NOSONAR

  // Remove event handlers
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, ''); //NOSONAR

  // Escape remaining HTML entities
  sanitized = escapeHtml(sanitized);

  return sanitized;
};

/**
 * Validate and sanitize a single form field
 */
export const validateField = (
  value: string,
  options: FieldValidationOptions = {}
): ValidationResult => {
  const errors: string[] = [];
  const fieldName = options.fieldName || 'Field';
  let sanitizedValue = sanitizeInput(value);

  // Required check
  if (options.required && !sanitizedValue) {
    errors.push(`${fieldName} is required`);
  }

  // Min length check
  if (options.minLength && sanitizedValue.length < options.minLength) {
    errors.push(`${fieldName} must be at least ${options.minLength} characters`);
  }

  // Max length check
  if (options.maxLength && sanitizedValue.length > options.maxLength) {
    sanitizedValue = sanitizedValue.substring(0, options.maxLength);
    errors.push(`${fieldName} must not exceed ${options.maxLength} characters`);
  }

  // Pattern check
  if (options.pattern && !options.pattern.test(sanitizedValue)) {
    errors.push(`${fieldName} format is invalid`);
  }

  // SQL injection check
  if (containsSqlInjection(value)) {
    errors.push(`${fieldName} contains potentially dangerous content`);
  }

  // XSS check
  if (containsXss(value)) {
    errors.push(`${fieldName} contains potentially dangerous content`);
  }

  // Code injection check
  if (containsCodeInjection(value)) {
    errors.push(`${fieldName} contains potentially dangerous content`);
  }

  // Custom validator
  if (options.customValidator && !options.customValidator(sanitizedValue)) {
    errors.push(`${fieldName} is invalid`);
  }

  return {
    isValid: errors.length === 0,
    sanitizedValue,
    errors,
  };
};

/**
 * Validate email format
 */
export const validateEmail = (email: string): ValidationResult => {
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const result = validateField(email, {
    required: true,
    pattern: emailPattern,
    fieldName: 'Email',
  });

  if (!emailPattern.test(email) && email.length > 0) {
    result.errors.push('Please enter a valid email address');
    result.isValid = false;
  }

  return result;
};

/**
 * Validate phone number (digits only)
 */
export const validatePhone = (phone: string): ValidationResult => {
  const phonePattern = /^[0-9+\-\s()]+$/;
  const result = validateField(phone, {
    pattern: phonePattern,
    minLength: 7,
    maxLength: 20,
    fieldName: 'Phone',
  });

  // Additional check to ensure it contains mostly digits
  const digitsOnly = phone.replace(/\D/g, ''); //NOSONAR
  if (digitsOnly.length < 7 && phone.length > 0) {
    result.errors.push('Phone number must have at least 7 digits');
    result.isValid = false;
  }

  return result;
};

/**
 * Validate name fields (no special characters except spaces, hyphens, and apostrophes)
 */
export const validateName = (name: string, fieldName = 'Name'): ValidationResult => {
  const namePattern = /^[a-zA-Z\s\-']+$/;
  return validateField(name, {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: namePattern,
    fieldName,
  });
};

/**
 * Validate message/text area content
 */
export const validateMessage = (
  message: string,
  maxLength = 1000
): ValidationResult => {
  return validateField(message, {
    maxLength,
    fieldName: 'Message',
  });
};

/**
 * Validate URL format
 */
export const validateUrl = (url: string): ValidationResult => {
  const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/; //NOSONAR
  const result = validateField(url, {
    pattern: urlPattern,
    fieldName: 'URL',
  });

  // Check for javascript: protocol
  if (url.toLowerCase().includes('javascript:')) { //NOSONAR
    result.errors.push('URL contains invalid protocol');
    result.isValid = false;
  }

  return result;
};

/**
 * Validate entire form data object
 */
export const validateFormData = <T extends Record<string, string>>(
  formData: T,
  validationRules: Record<keyof T, FieldValidationOptions>
): { isValid: boolean; errors: Record<string, string[]>; sanitizedData: T } => {
  const errors: Record<string, string[]> = {};
  const sanitizedData: Record<string, string> = {};
  let isValid = true;

  for (const [key, value] of Object.entries(formData)) {
    const rules = validationRules[key as keyof T] || {};
    const result = validateField(value, { ...rules, fieldName: rules.fieldName || key });

    if (!result.isValid) {
      isValid = false;
      errors[key] = result.errors;
    }

    sanitizedData[key] = result.sanitizedValue;
  }

  return {
    isValid,
    errors,
    sanitizedData: sanitizedData as T,
  };
};

/**
 * Check if a file type is allowed
 */
export const validateFileType = (
  file: File,
  allowedTypes: string[]
): ValidationResult => {
  const errors: string[] = [];
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
  const mimeType = file.type.toLowerCase();

  const isValidType =
    allowedTypes.some((type) => type.toLowerCase() === fileExtension) ||
    allowedTypes.some((type) => mimeType.includes(type.toLowerCase()));

  if (!isValidType) {
    errors.push(`File type not allowed. Allowed types: ${allowedTypes.join(', ')}`);
  }

  return {
    isValid: errors.length === 0,
    sanitizedValue: file.name,
    errors,
  };
};

/**
 * Check if file size is within limit
 */
export const validateFileSize = (
  file: File,
  maxSizeInBytes: number
): ValidationResult => {
  const errors: string[] = [];
  const maxSizeMB = maxSizeInBytes / (1024 * 1024);

  if (file.size > maxSizeInBytes) {
    errors.push(`File size must be less than ${maxSizeMB}MB`);
  }

  return {
    isValid: errors.length === 0,
    sanitizedValue: file.name,
    errors,
  };
};

export default {
  escapeHtml,
  sanitizeInput,
  containsSqlInjection,
  containsXss,
  containsCodeInjection,
  validateField,
  validateEmail,
  validatePhone,
  validateName,
  validateMessage,
  validateUrl,
  validateFormData,
  validateFileType,
  validateFileSize,
};
